import type { ConditionDto } from "@repo/data-store";
import { PropertyPicker, PropertyPickerCalendar, type PropertyPickerOption } from "@repo/ui/components/property-picker";
import { Label } from "@repo/ui/components/ui/label";
import { CalendarDays } from "lucide-react";
import { EMPTY_GOAL_FORM } from "@/features/cases/forms/use-goal-form";
import { withForm } from "@/shared/forms/app-form";
import { TaskPriorityIcon } from "@/shared/status/task-priority-icon";

// Goal priority pill picker — same field as the task priority picker.
// `TaskPriorityIcon` keys on task-priority codes: routine→none, urgent→low, asap→medium, stat→high.
const GOAL_PRIORITY_OPTIONS: PropertyPickerOption[] = [
  { value: "none", label: "None", icon: <TaskPriorityIcon priority="routine" size={14} /> },
  { value: "low", label: "Low", icon: <TaskPriorityIcon priority="urgent" size={14} /> },
  { value: "medium", label: "Medium", icon: <TaskPriorityIcon priority="asap" size={14} /> },
  { value: "high", label: "High", icon: <TaskPriorityIcon priority="stat" size={14} /> },
];

const TARGET_KIND_OPTIONS = [
  { value: "none", label: "No target" },
  { value: "quantity", label: "Measurable" },
  { value: "text", label: "Descriptive" },
];

const COMPARATOR_OPTIONS = [
  { value: "", label: "Exactly" },
  { value: "<", label: "Less than" },
  { value: "<=", label: "At most" },
  { value: ">=", label: "At least" },
  { value: ">", label: "Greater than" },
];

interface GoalFormFieldsProps {
  /** Conditions selectable in the addresses list — the care plan's targeted conditions. */
  conditionOptions: ConditionDto[];
}

/** Shared goal form fields for the add and edit dialogs (bound to `GoalFormValues`). */
export const GoalFormFields = withForm({
  defaultValues: EMPTY_GOAL_FORM,
  props: {} as GoalFormFieldsProps,
  render: function Render({ form, conditionOptions }) {
    return (
      <div className="space-y-4">
        <form.AppField name="description">
          {(field) => <field.TextareaField label="Description" placeholder="What should this plan achieve?" />}
        </form.AppField>

        <div className="grid grid-cols-2 gap-3">
          <form.AppField name="priority">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label>Priority</Label>
                <field.PropertyPickerListField options={GOAL_PRIORITY_OPTIONS} />
              </div>
            )}
          </form.AppField>
          <form.AppField name="startDate">
            {(field) => (
              <div className="flex flex-col gap-1.5">
                <Label>Start date</Label>
                <PropertyPicker<Date>
                  value={field.state.value}
                  onChange={(d) => field.handleChange(d)}
                  placeholder="Set start date"
                  icon={<CalendarDays className="size-3.5" />}
                  displayValue={
                    field.state.value
                      ? field.state.value.toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : null
                  }
                >
                  <PropertyPickerCalendar selected={field.state.value ?? undefined} mode="single" />
                </PropertyPicker>
              </div>
            )}
          </form.AppField>
        </div>

        {conditionOptions.length > 0 && (
          <form.AppField name="addresses">
            {(field) => (
              <field.SwitchListField
                label="Conditions addressed"
                options={conditionOptions.map((c) => ({
                  value: c.conditionId,
                  label: c.code.display ?? c.code.text ?? c.code.code,
                }))}
              />
            )}
          </form.AppField>
        )}

        <div className="space-y-3 rounded-md border p-3">
          <form.AppField name="targetKind">
            {(field) => <field.SelectField label="Target" options={TARGET_KIND_OPTIONS} />}
          </form.AppField>

          <form.Subscribe selector={(s) => s.values.targetKind}>
            {(kind) =>
              kind === "none" ? null : (
                <div className="space-y-3">
                  <form.AppField name="targetMeasure">
                    {(field) => <field.InputField label="Measure" placeholder="e.g. Hemoglobin A1c" />}
                  </form.AppField>

                  {kind === "quantity" ? (
                    <div className="grid grid-cols-3 gap-2">
                      <form.AppField name="targetComparator">
                        {(field) => <field.SelectField label="Comparator" options={COMPARATOR_OPTIONS} />}
                      </form.AppField>
                      <form.AppField name="targetValue">
                        {(field) => <field.NumberField label="Value" step="any" />}
                      </form.AppField>
                      <form.AppField name="targetUnit">
                        {(field) => <field.InputField label="Unit" placeholder="%" />}
                      </form.AppField>
                    </div>
                  ) : (
                    <form.AppField name="targetText">
                      {(field) => <field.InputField label="Target" placeholder="e.g. walks unassisted" />}
                    </form.AppField>
                  )}

                  <form.AppField name="targetDue">
                    {(field) => (
                      <div className="flex flex-col gap-1.5">
                        <Label>Due date</Label>
                        <PropertyPicker<Date>
                          value={field.state.value}
                          onChange={(d) => field.handleChange(d)}
                          placeholder="Set due date"
                          icon={<CalendarDays className="size-3.5" />}
                          displayValue={
                            field.state.value
                              ? field.state.value.toLocaleDateString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                })
                              : null
                          }
                        >
                          <PropertyPickerCalendar selected={field.state.value ?? undefined} mode="single" />
                        </PropertyPicker>
                      </div>
                    )}
                  </form.AppField>
                </div>
              )
            }
          </form.Subscribe>
        </div>
      </div>
    );
  },
});
